import axios from "axios";
import { toast } from "react-toastify";
import { store, forceLogout } from "../Store";
import { clearMessages } from "../Store/Slices/ChatSocketSlice";
import { getErrorMessage } from "../Utils/errorMessage";
import { API_BASE } from "./config";

export const BACKEND_BASE_URL = API_BASE;

const http = axios.create({
  baseURL: BACKEND_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Touchpoint": "CHAT"
  },
});

const onRequest = (config) => {
  const token = store.getState().auth.token;

  // Skip auth header for login/register — stale JWT causes 406 on token endpoints
  const isAuthEndpoint = config.url?.includes("/token") || config.url?.includes("/register");
  if (token && token !== "" && !config.headers?.Authorization && !isAuthEndpoint) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
};

const onRequestError = (error) => {
  return Promise.reject(new Error(error));
};

const onResponse = (response) => {
  return response;
};

export const onResponseError = (error) => {
  // Skip toast for requests marked as silent
  //
  // This return is also what keeps a silent request away from the 401 handling
  // further down, and something depends on that: the guest handover runs just
  // after a visitor signs up, and a 401 from it must not clear the account they
  // have just created. Moving this block below the 401 block would sign that
  // customer straight back out.
  if (error.config?.silentError) {
    return Promise.reject(error);
  }

  // Network error or no response from server
  if (!error.response) {
    // Don't toast for network errors on background requests
    if (!error.config?.backgroundRequest) {
      toast.error("Unable to connect. Please check your network.");
    }
    return Promise.reject(error);
  }

  const status = error.response.status;
  const data = error.response.data;

  // Show specific error message based on response
  if (data?.message === "Invalid OTP") {
    toast.error(getErrorMessage(data.message));
  } else if (data?.error) {
    toast.error(getErrorMessage(data.error));
  } else if (data?.message) {
    toast.error(getErrorMessage(data.message));
  }
  // No generic fallback toast — only toast when we have actual error info

  // A 401 normally means the session this request was made with has run out, so
  // the session is cleared and the visitor starts again. On a sign-in attempt it
  // means something else entirely: the password was wrong. There is no stale
  // session to clear, and clearing anyway threw away the conversation the
  // visitor was in the middle of — one mistyped password and their messages
  // were gone. Requests that can legitimately be answered 401 say so.
  if (status === 401 && !error.config?.expectedUnauthorized) {
    // Clear session on unauthorized response
    store.dispatch(clearMessages());
    store.dispatch(forceLogout());
  }

  return Promise.reject(error);
};

http.interceptors.request.use(onRequest, onRequestError);
http.interceptors.response.use(onResponse, onResponseError);

export default http;
